import * as R from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import React, { useMemo, useEffect, useCallback } from 'react';
// components
import { Table } from '../../../components/table';
import { TextComponent } from '../../../components/text';
import { ConfirmComponent } from '../../../components/confirm';
// features
import CompensationForm from '../../../forms/forms/compensation';
import { compensationSettings } from '../../fleet-profile/settings/compensation';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hooks
import useEditViewHook from '../../../hooks/use-edit-view';
import { useAsyncGetConfigs, useLoaderActions, useModalActions } from '../../../hooks';
// ui
import { RelativeBox, MainActionButton } from '../../../ui';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// feature compensation-group
import {
  makeSelectBranchConfigs,
  makeSelectCompensationGroupByGroupGuid,
} from '../selectors';
import {
  setBranchConfigs,
  getCompensationGroupByGuidSuccess,
  removeCompensationGroupCompensationRequest,
  createOrUpdateCompensationGroupCompensationRequest,
} from '../actions';
//////////////////////////////////////////////////

const { filterProps } = compensationSettings;

const TableCell = ({ text }) => (
  <TextComponent
    title={text}
    maxWidth='90%'
    display='block'
    withEllipsis={true}
  >
    {text}
  </TextComponent>
);

const columnSettings = R.compose(
  R.assocPath([GC.GRC.SERVICE_TYPE_DISPLAYED_VALUE, 'customComponent'], ({ data }) => {
    const text = R.pathOr('', [GC.FIELD_SERVICE_TYPE, GC.FIELD_DISPLAYED_VALUE], data);

    return <TableCell text={text} />;
  }),
  R.assocPath([GC.GRC.MODE_DISPLAYED_VALUE, 'customComponent'], ({ data }) => {
    const text = R.pathOr('', [GC.FIELD_MODE, GC.FIELD_DISPLAYED_VALUE], data);

    return <TableCell text={text} />;
  }),
  R.prop('columnSettings'),
)(compensationSettings);

const report = {
  type: GC.FLEET_COMPENSATION_GROUP_REPORT,
  [GC.FIELD_GUID]: GC.FLEET_COMPENSATION_GROUP_REPORT,
  fields: G.mapIndexed((name, sequence) => ({ name, sequence }), R.keys(columnSettings)),
};

const tableSettings = {
  maxHeight: 600,
  cellFontSize: 12,
  titleFontSize: 12,
  titleRowHeight: 50,
  allowEditBtn: true,
  tableRowHeight: 32,
  useMainColors: true,
  allowSelectAll: false,
  checkBoxCellWidth: 54,
  searchableTitles: true,
  allowSelectItems: false,
  emptyListPadding: '10px 0',
  checkBoxCellJustifyContent: 'flex-start',
};

const useGetCompensationGroupByGuid = (guid) => {
  const dispatch = useDispatch();

  const { openLoader, closeLoader } = useLoaderActions();

  const compensationGroup = useSelector((state) => makeSelectCompensationGroupByGroupGuid(state, guid));

  const { compensations } = compensationGroup;

  useEffect(() => {
    let isMounted = true;

    const handleGetCompensationsByGroupGuid = async () => {
      if (R.or(R.isNil(guid), G.isNotNilAndNotEmpty(compensations))) return;

      openLoader();

      try {
        const res = await sendRequest('get', endpointsMap.compensationGroupByGuid(guid));

        if (!isMounted) return;

        const { data, status } = res;

        if (G.isResponseSuccess(status)) {
          dispatch(getCompensationGroupByGuidSuccess(data));
        } else {
          G.handleFailResponseSimple(res, true, 'handleGetCompensationsByGroupGuid fail');
        }
      } catch (error) {
        if (isMounted) {
          G.showToastrMessage('error', 'messages:error:unknown');
          G.handleException(error, 'handleGetCompensationsByGroupGuid exception');
        }
      } finally {
        closeLoader();
      }
    };

    handleGetCompensationsByGroupGuid();

    return () => {
      isMounted = false;
    };
  }, [guid, compensations]);

  return compensationGroup;
};

const useCompensationTableActions = (props) => {
  const { isReadOnlyMode, setCompensations, compensationGroup, compensationGroupGuid } = props;

  const dispatch = useDispatch();

  const { openModal } = useModalActions();

  const branchConfigs = useSelector(makeSelectBranchConfigs);

  const asyncConfigs = useAsyncGetConfigs({
    createOptionsFromDropdowns: true,
    shouldNotGetConfigs: G.isNotNilAndNotEmpty(branchConfigs),
    initialValues: G.isNotNilAndNotEmpty(branchConfigs) ? branchConfigs : {},
    names: [
      GC.GENERAL_MODE_TRANSPORTATION,
      GC.GENERAL_BRANCH_DEFAULT_CURRENCY,
      GC.GENERAL_TRANSPORTATION_SERVICE_TYPE,
    ],
  });

  const handleRemoveCompensation = useCallback((guid, { id, name } = {}) => {
    const action = () => {
      if (G.isFunction(setCompensations)) {
        setCompensations(id, { shouldCloseModal: true });
      } else {
        dispatch(removeCompensationGroupCompensationRequest({ guid, compensationGroupGuid }));
      }
    };

    const component = (
      <ConfirmComponent
        name={R.or(name, G.getWindowLocale('titles:compensation-details', 'Compensation Details'))}
        textLocale={G.getWindowLocale('messages:before:remove', 'Are you sure you want to remove')}
      />
    );

    const modal = {
      component,
      options: {
        width: 600,
        controlButtons: [
          {
            action,
            type: 'button',
            name: G.getWindowLocale('actions:delete', 'Delete'),
          },
        ],
      },
    };

    openModal(modal);
  }, [compensationGroup]);

  const handleCreateOrUpdateCompensation = useCallback((_, entity) => {
    const { id } = R.or(entity, {});

    const initialValues = {
      ...entity,
      [GC.FIELD_ID]: R.isNotNil(id) ? id : G.generateShortId(),
      [GC.FIELD_MODE]: R.pathOr(
        R.propOr('', GC.FIELD_MODE, entity),
        [GC.FIELD_MODE, GC.FIELD_DROPDOWN_OPTION_GUID],
        entity,
      ),
      [GC.FIELD_SERVICE_TYPE]: R.pathOr(
        R.propOr('', GC.FIELD_SERVICE_TYPE, entity),
        [GC.FIELD_SERVICE_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID],
        entity,
      ),
      [GC.FIELD_CURRENCY]: R.propOr(
        G.getConfigValueFromStore(GC.GENERAL_BRANCH_DEFAULT_CURRENCY, asyncConfigs),
        GC.FIELD_CURRENCY,
        entity,
      ),
    };

    const submitAction = (values, additionalOptions) => {
      if (G.isFunction(setCompensations)) {
        setCompensations(values, additionalOptions);
      } else {
        dispatch(createOrUpdateCompensationGroupCompensationRequest({
          ...values,
          additionalOptions,
          compensationGroupGuid,
        }));
      }
    };

    const { dropdownsOptions } = asyncConfigs;

    const optionsForSelect = {
      [GC.FIELD_MODE]: G.addEmptyOptionToDropDown(R.prop(GC.GENERAL_MODE_TRANSPORTATION, dropdownsOptions)),
      [GC.FIELD_SERVICE_TYPE]: G.addEmptyOptionToDropDown(
        R.prop(GC.GENERAL_TRANSPORTATION_SERVICE_TYPE, dropdownsOptions),
      ),
    };

    const component = (
      <CompensationForm
        submitAction={submitAction}
        initialValues={initialValues}
        isReadOnlyMode={isReadOnlyMode}
        fleetType={GC.USER_TYPE_DRIVER}
        optionsForSelect={optionsForSelect}
        primaryObjectGuidKey={GC.FIELD_GUID}
        isEditMode={G.isNotNilAndNotEmpty(entity)}
        additionalLocationFields={GC.GROUPED_FIELDS.API_DEFAULT_FIELDS}
      />
    );

    const title = G.ifElse(
      G.isNotNilAndNotEmpty(entity),
      G.getEditTitle,
      G.getAddTitle,
    )(['titles:compensation-details', 'Compensation Details']);

    const modal = {
      p: 15,
      component,
      options: { title },
    };

    openModal(modal);
  }, [asyncConfigs, setCompensations, compensationGroup]);

  useEffect(() => {
    if (R.and(G.isNilOrEmpty(branchConfigs), G.isNotNilAndNotEmpty(asyncConfigs))) {
      dispatch(setBranchConfigs(asyncConfigs));
    }
  }, [asyncConfigs]);

  const defaultActionButtons = [
    {
      permissions: null,
      iconName: 'pencil',
      action: handleCreateOrUpdateCompensation,
      title: G.getWindowLocale('actions:edit', 'Edit'),
    },
    {
      iconName: 'trash',
      permissions: null,
      action: handleRemoveCompensation,
      title: G.getWindowLocale('actions:delete', 'Delete'),
    },
  ];

  const readOnlyActionButtons = [
    {
      iconName: 'eye2',
      permissions: null,
      action: handleCreateOrUpdateCompensation,
      title: G.getWindowLocale('actions:show-details', 'Show Details'),
    },
  ];

  const actionButtons = isReadOnlyMode ? readOnlyActionButtons : defaultActionButtons;

  return {
    actionButtons,
    branchConfigs,
    handleCreateOrUpdateCompensation: isReadOnlyMode ? null : handleCreateOrUpdateCompensation,
  };
};

const Compensations = (props) => {
  const {
    m,
    itemList,
    maxWidth,
    actionButtons,
    branchConfigs,
    handleCreateOrUpdateCompensation,
  } = props;

  const { uiReportFields, handleOpenEditView } = useEditViewHook({
    report,
    columnSettings,
    defaultReportFields: R.keys(columnSettings),
  });

  const sortedReport = useMemo(() => (
    G.isNilOrEmpty(uiReportFields) ? report : G.getSortedReport(uiReportFields, report)
  ), [uiReportFields]);

  return (
    <RelativeBox zIndex='0' m={R.or(m, 25)} maxWidth={maxWidth}>
      {
        G.isFunction(handleCreateOrUpdateCompensation) &&
        <MainActionButton
          mb={5}
          type='button'
          disabled={R.isNil(branchConfigs)}
          onClick={() => handleCreateOrUpdateCompensation()}
        >
          {G.getAddTitle(['titles:compensation-details', 'Compensation Details'])}
        </MainActionButton>
      }
      <Table
        showEditView={true}
        report={sortedReport}
        shouldUseUIFilters={true}
        filterProps={filterProps}
        useSearchableColumns={true}
        withResizableColumns={true}
        tableSettings={tableSettings}
        actionButtons={actionButtons}
        itemList={R.or(itemList, [])}
        useNewTitleFilterInputs={true}
        columnSettings={columnSettings}
        handleClickEditOrder={handleOpenEditView}
      />
    </RelativeBox>
  );
};

const CompensationsWithActions = ({ guid, maxWidth, isReadOnlyMode }) => {
  const compensationGroup = useGetCompensationGroupByGuid(guid);
  const tableActions = useCompensationTableActions({ isReadOnlyMode, compensationGroupGuid: guid });

  return (
    <Compensations
      {...tableActions}
      maxWidth={maxWidth}
      itemList={R.prop('compensations', compensationGroup)}
    />
  );
};

export {
  Compensations,
  CompensationsWithActions,
  useCompensationTableActions,
  useGetCompensationGroupByGuid,
};
